import { Component, Input, Injector, OnInit } from '@angular/core';
import { take } from 'rxjs/operators';
import { WidgetBaseComponent } from '../../widgetBase';
import { ObjectUtil } from '../../../../../components/common/object-util/object-util';

/**
 * ValuationResults3 Widget
 */
@Component({
  selector: 'valuation-results3-widget',
  templateUrl: './valuation-results3-widget.component.html',
  styleUrls: ['../../common/css/general.scss', './valuation-results3-widget.component.scss']
})
export class ValuationResults3WidgetComponent extends WidgetBaseComponent implements OnInit {
  /**
   * The initial data
   */
  private initialData: any;

  /**
   * Widget Guid
   */
  @Input() widgetGuid: string;

  /**
   * Show costs of sale?
   */
  public showCostsOfSale: boolean;

  /**
   * Constructor
   * @param injector
   */
  constructor(injector: Injector) {
    super(injector, 'valuationResults3', false);
  }

  /**
   * Initializes the component
   */
  ngOnInit(): void {
    this.initWidgetConfig();
    this.widgetResized();
  }

  /**
   * Called by base class to let widget register to various app events (like saving, loading, resizing etc)
   */
  registerEvents(): void {
    this.useDefaultServiceSaveChangesHandler("valuationResults3", this.provideSaveObject.bind(this));

    //page.editMode does not update without this (ChangeDetectionStrategy.OnPush)
    this.onEvents.push(this.eventBus.subscribeEvent('editModeToggled', () => {
      this.ref.markForCheck();
    }));
  }

  /**
   * Setup widget's layout if required
   */
  setupLayouts() {
    const fixLayouts = this.availableLayouts.length === 0 && this.currentLayout === 'default';

    if (fixLayouts) {
      this.currentLayout = 'DCF';
    }
  }

  /**
   * Called by base class to inform widget that it is initialized
   * Mostly used to define where widget data should be loaded from
   */
  onInitialized(): void {
    this.setupLayouts();

    this.mockupData$ = this.widgetService.loadMockup('valuationResults3').pipe(take(1));

    if (!this.widgetConfig.widgetSettings.buttonCostsOfSale) {
      console.log('ValuationResults3WidgetComponent: Upgrading widget settings: Set widgetConfig.widgetSettings.buttonCostsOfSale');
      this.widgetConfig.widgetSettings.buttonCostsOfSale = { show: true };
    }
    this.showCostsOfSale = this.widgetConfig.widgetSettings.buttonCostsOfSale.show;

    this.widgetData$ = this.mockup ?
      this.mockupData$
      : this.widgetService.load('valuationResults3', this.getReport()).pipe();
  }

  /**
   * Called by base class to let widget preprocess loaded data if required
   */
  preprocessLoadedData(data) {
    this.initialData = ObjectUtil.deepCopy(data);
    this.checkManualOverrides(data);
    return data;
  }

  /**
   * Called by base class in save process. Widget should return object to be saved
   */
  async provideSaveObject(): Promise<any> {
    return this.widgetData;
  }

  /**
   * Layout changed event handler
  * @param newLayout {string} New layout
   */
  public layoutChanged(newLayout: string): void {
    try {
      if (this.widgetConfig?.widgetSettings?.layoutSizeConfiguration) {
        const config = this.widgetConfig.widgetSettings.layoutSizeConfiguration[newLayout];
        if (config.x) {
          this.widgetConfig.minSizeX = config.x;
          this.widgetConfig.sizeX = config.x;
        }
        if (config.y) {
          this.widgetConfig.sizeY = config.y;
        }
      }
      this.widgetConfig.widgetSettings.currentLayout = newLayout;
    } catch (e) {
      // Nothing
    }
  }

  /**
   * Checks manual override fields
   * @param data
   */
  public checkManualOverrides(data: any): void {
    let dataToUse = data;
    if (!dataToUse) {
      dataToUse = this.widgetData;
    }

    this.checkManualOverridesDcf(dataToUse);
    this.checkManualOverridesTradVal(dataToUse);

    this.ref.markForCheck();
  }

  /**
   * Apply a manual value
   * @param property
   * @param value
   */
  public applyManualValue(property: string, value: number){
      this.widgetData[property] = value / 100;
  }

  /**
   * Checks manual override fields for TradVal
   * @param dataToUse
   */
  private checkManualOverridesTradVal(dataToUse: any): void {
    if (dataToUse.tradValGrossInitialYieldManual) {
      dataToUse.tradValGrossInitialYield = dataToUse.tradValGrossInitialYieldManual;
    }
    else if (this.initialData) {
      dataToUse.tradValGrossInitialYield = this.initialData.tradValGrossInitialYield;
    }

    if (dataToUse.tradValNetInitialYieldManual) {
      dataToUse.tradValNetInitialYield = dataToUse.tradValNetInitialYieldManual;
    }
    else if (this.initialData) {
      dataToUse.tradValNetInitialYield = this.initialData.tradValNetInitialYield;
    }

    if (dataToUse.tradValGrossReversionaryYieldManual) {
      dataToUse.tradValGrossReversionaryYield = dataToUse.tradValGrossReversionaryYieldManual;
    }
    else if (this.initialData) {
      dataToUse.tradValGrossReversionaryYield = this.initialData.tradValGrossReversionaryYield;
    }

    if (dataToUse.tradValNetReversionaryYieldManual) {
      dataToUse.tradValNetReversionaryYield = dataToUse.tradValNetReversionaryYieldManual;
    }
    else if (this.initialData) {
      dataToUse.tradValNetReversionaryYield = this.initialData.tradValNetReversionaryYield;
    }

    //tradValEquivalentYield is pure manually
  }

  /**
   * Checks manual override fields for DCF
   * @param dataToUse
   */
  private checkManualOverridesDcf(dataToUse: any): void {
    if (dataToUse.netInitialYieldDCFManual) {
      dataToUse.netInitialYieldDCF = dataToUse.netInitialYieldDCFManual;
    }
    else if (this.initialData) {
      dataToUse.netInitialYieldDCF = this.initialData.netInitialYieldDCF;
    }

    if (dataToUse.reversionaryYieldDCFManual) {
      dataToUse.reversionaryYieldDCF = dataToUse.reversionaryYieldDCFManual;
    }
    else if (this.initialData) {
      dataToUse.reversionaryYieldDCF = this.initialData.reversionaryYieldDCF;
    }

    if (dataToUse.initialYieldDCFManual) {
      dataToUse.initialYieldDCF = dataToUse.initialYieldDCFManual;
    }
    else if (this.initialData) {
      dataToUse.initialYieldDCF = this.initialData.initialYieldDCF;
    }

    if (dataToUse.grossReversionaryYieldDCFManual) {
      dataToUse.grossReversionaryYieldDCF = dataToUse.grossReversionaryYieldDCFManual;
    }
    else if (this.initialData) {
      dataToUse.grossReversionaryYieldDCF = this.initialData.grossReversionaryYieldDCF;
    }
  }

  /**
   * Show or hide the costs of sale button
   * @param show
   */
  public toggleButtonCostsOfSale(show: boolean) {
    this.widgetConfig.widgetSettings.buttonCostsOfSale.show = show;
    this.showCostsOfSale = show;
  }
}
